import React, { useState, useEffect } from 'react';
import { PROVINCE_TAX_RATES, TAX_MODULE_DEFAULTS, calculateCanadianTax, getEffectiveRates } from './canadianTax';
import { ContextualAgencyTrigger } from '../components/dashboard/AgencyUpsellManager.jsx';

const PROVINCES = Object.entries(PROVINCE_TAX_RATES).map(([code, r]) => ({ code, name: r.name, taxType: r.taxType }));

const TAX_TYPE_BADGE = {
    'hst':      { label: 'HST',         bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
    'gst+pst':  { label: 'GST + PST',   bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' },
    'gst+qst':  { label: 'GST + QST',   bg: '#fdf4ff', color: '#6b21a8', border: '#e9d5ff' },
    'gst':      { label: 'GST Only',    bg: '#fff7ed', color: '#c2410c', border: '#fed7aa' },
};

const inputStyle = {
    width: '100%', padding: '9px 12px', borderRadius: '7px',
    border: '1px solid #cbd5e1', fontSize: '0.875rem', background: '#fff',
    boxSizing: 'border-box',
};
const labelStyle = { display: 'block', fontSize: '0.78rem', fontWeight: '700', color: '#475569', marginBottom: '4px' };
const sectionCard = { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '18px', display: 'flex', flexDirection: 'column', gap: '14px' };

/**
 * Format override component rate for text/number input display without IEEE 754 precision noise
 */
const getComponentOverrideDisplay = (override, component, taxType) => {
    if (!override) return '';
    if (component === 'gst') {
        if (override.gstPercent !== undefined && override.gstPercent !== null && override.gstPercent !== '') {
            return override.gstPercent;
        }
        if (override.gst !== undefined && typeof override.gst === 'number') {
            return parseFloat(((override.gst || 0) * 100).toFixed(4)).toString();
        }
        return '';
    }
    if (component === 'provincial') {
        if (taxType === 'hst') {
            if (override.hstPercent !== undefined && override.hstPercent !== null && override.hstPercent !== '') {
                return override.hstPercent;
            }
            if (override.hst !== undefined && typeof override.hst === 'number') {
                return parseFloat(((override.hst || 0) * 100).toFixed(4)).toString();
            }
            if (override.ratePercent !== undefined && override.ratePercent !== null && override.ratePercent !== '') {
                return override.ratePercent;
            }
            return '';
        }
        if (taxType === 'gst+qst') {
            if (override.qstPercent !== undefined && override.qstPercent !== null && override.qstPercent !== '') {
                return override.qstPercent;
            }
            if (override.qst !== undefined && typeof override.qst === 'number') {
                return parseFloat(((override.qst || 0) * 100).toFixed(4)).toString();
            }
            return '';
        }
        // gst+pst
        if (override.pstPercent !== undefined && override.pstPercent !== null && override.pstPercent !== '') {
            return override.pstPercent;
        }
        if (override.pst !== undefined && typeof override.pst === 'number') {
            return parseFloat(((override.pst || 0) * 100).toFixed(4)).toString();
        }
        return '';
    }
    return '';
};

export const CanadianTaxManager = ({ config, onSave, onUpdate }) => {
    const modules = config?.modules || {};
    const taxCfg  = { ...TAX_MODULE_DEFAULTS, ...(modules.canadianTax || {}) };

    const [cfg, setCfg]         = useState(taxCfg);
    const [previewProv, setPreviewProv] = useState('ON');
    const [saved, setSaved]     = useState('');

    useEffect(() => {
        const cur = config?.modules?.canadianTax;
        if (cur && typeof cur === 'object') {
            setCfg(prev => ({ ...TAX_MODULE_DEFAULTS, ...prev, ...cur }));
        }
    }, [config]);

    const update = (patch) => {
        setCfg(prev => {
            const next = { ...prev, ...patch };
            if (typeof onUpdate === 'function') {
                const nextConfig = { ...config, modules: { ...(config?.modules || {}), canadianTax: next } };
                onUpdate(nextConfig);
            }
            return next;
        });
    };

    const updateCustomComponent = (code, component, rawVal) => {
        const nextCustomRates = { ...(cfg.customRates || {}) };
        const existing = { ...(nextCustomRates[code] || {}) };
        const base = PROVINCE_TAX_RATES[code] || {};

        if (rawVal === undefined || rawVal === null || rawVal.trim() === '') {
            if (component === 'gst') {
                delete existing.gst;
                delete existing.gstPercent;
            } else if (component === 'pst') {
                delete existing.pst;
                delete existing.pstPercent;
            } else if (component === 'hst') {
                delete existing.hst;
                delete existing.hstPercent;
            } else if (component === 'qst') {
                delete existing.qst;
                delete existing.qstPercent;
            }
            delete existing.ratePercent;
            delete existing.totalRate;

            const hasRemaining =
                existing.gst !== undefined || existing.pst !== undefined ||
                existing.hst !== undefined || existing.qst !== undefined ||
                (existing.gstPercent !== undefined && existing.gstPercent !== '') ||
                (existing.pstPercent !== undefined && existing.pstPercent !== '') ||
                (existing.hstPercent !== undefined && existing.hstPercent !== '') ||
                (existing.qstPercent !== undefined && existing.qstPercent !== '');

            if (!hasRemaining) {
                delete nextCustomRates[code];
            } else {
                const g = existing.gst !== undefined ? existing.gst : (base.gst || 0);
                const p = existing.pst !== undefined ? existing.pst : (base.pst || 0);
                const h = existing.hst !== undefined ? existing.hst : (base.hst || 0);
                const q = existing.qst !== undefined ? existing.qst : (base.qst || 0);
                const tot = base.taxType === 'hst' ? h : (g + p + q);
                existing.totalRate = Math.round(tot * 1000000) / 1000000;
                existing.ratePercent = parseFloat((tot * 100).toFixed(4)).toString();
                nextCustomRates[code] = existing;
            }
            update({ customRates: nextCustomRates });
            return;
        }

        const strVal = rawVal.trim();
        const numVal = parseFloat(strVal);
        const decimalVal = (!isNaN(numVal) && numVal >= 0) ? (Math.round((numVal / 100) * 1000000) / 1000000) : null;

        if (component === 'gst') {
            existing.gstPercent = rawVal;
            if (decimalVal !== null) existing.gst = decimalVal;
        } else if (component === 'pst') {
            existing.pstPercent = rawVal;
            if (decimalVal !== null) existing.pst = decimalVal;
        } else if (component === 'hst') {
            existing.hstPercent = rawVal;
            if (decimalVal !== null) existing.hst = decimalVal;
        } else if (component === 'qst') {
            existing.qstPercent = rawVal;
            if (decimalVal !== null) existing.qst = decimalVal;
        }

        const g = existing.gst !== undefined ? existing.gst : (base.gst || 0);
        const p = existing.pst !== undefined ? existing.pst : (base.pst || 0);
        const h = existing.hst !== undefined ? existing.hst : (base.hst || 0);
        const q = existing.qst !== undefined ? existing.qst : (base.qst || 0);
        const tot = base.taxType === 'hst' ? h : (g + p + q);
        existing.totalRate = Math.round(tot * 1000000) / 1000000;
        existing.ratePercent = parseFloat((tot * 100).toFixed(4)).toString();

        nextCustomRates[code] = existing;
        update({ customRates: nextCustomRates });
    };

    const resetProvinceOverride = (code) => {
        const nextCustomRates = { ...(cfg.customRates || {}) };
        delete nextCustomRates[code];
        update({ customRates: nextCustomRates });
    };

    const getProvinceEffectiveTotalDisplay = (code) => {
        const eff = getEffectiveRates(code, cfg);
        const sum = ((eff.hst || 0) + (eff.gst || 0) + (eff.pst || 0) + (eff.qst || 0)) * 100;
        return parseFloat(sum.toFixed(4)).toString() + '%';
    };

    const handleSave = () => {
        const newConfig = { ...config, modules: { ...(config?.modules || {}), canadianTax: cfg } };
        onSave(newConfig);
        setSaved('✅ Canadian Tax settings saved!');
        setTimeout(() => setSaved(''), 3000);
    };

    useEffect(() => {
        const handleGlobalSave = () => {
            handleSave();
        };
        window.addEventListener('dashboard-save-all', handleGlobalSave);
        window.addEventListener('modules-save-all', handleGlobalSave);
        return () => {
            window.removeEventListener('dashboard-save-all', handleGlobalSave);
            window.removeEventListener('modules-save-all', handleGlobalSave);
        };
    }, [cfg, config, onSave]);

    const storeRates  = PROVINCE_TAX_RATES[cfg.storeProvince] || {};
    const previewResult = calculateCanadianTax(100, previewProv, cfg);
    const previewBadge  = TAX_TYPE_BADGE[PROVINCE_TAX_RATES[previewProv]?.taxType] || TAX_TYPE_BADGE['gst'];

    return (
        <div style={{ maxWidth: '780px', fontFamily: 'Inter, system-ui, sans-serif', display: 'flex', flexDirection: 'column', gap: '22px' }}>

            {/* Header */}
            <div>
                <h3 style={{ margin: '0 0 4px 0', fontSize: '1.35rem', color: '#0f172a', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    🍁 Canadian Tax Engine
                </h3>
                <p style={{ margin: 0, color: '#64748b', fontSize: '0.88rem', lineHeight: '1.55' }}>
                    Automatically calculate and display GST, HST, PST, and QST based on the store's registered province and the customer's shipping address. Follows CRA "place of supply" rules.
                </p>
                <ContextualAgencyTrigger context="ecommerce_tax" config={config} />
            </div>

            {/* Master Enable Toggle */}
            <div style={{
                ...sectionCard,
                border: `2px solid ${cfg.enabled ? '#10b981' : '#e2e8f0'}`,
                background: cfg.enabled ? '#f0fdf4' : '#f8fafc',
                flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px'
            }}>
                <div>
                    <div style={{ fontWeight: '700', fontSize: '1rem', color: cfg.enabled ? '#065f46' : '#334155' }}>
                        {cfg.enabled ? '✅ Canadian Tax Collection: ENABLED' : '○ Canadian Tax Collection: DISABLED'}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '3px' }}>
                        When enabled, tax is calculated at checkout for Canadian customers and shown in the order summary.
                    </div>
                </div>
                <button
                    onClick={() => update({ enabled: !cfg.enabled })}
                    style={{
                        background: cfg.enabled ? '#ef4444' : '#10b981',
                        color: '#fff', border: 'none', padding: '10px 22px',
                        borderRadius: '7px', fontWeight: '700', fontSize: '0.9rem', cursor: 'pointer',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.1)', whiteSpace: 'nowrap'
                    }}
                >
                    {cfg.enabled ? 'Disable Tax Module' : 'Enable Tax Module'}
                </button>
            </div>

            {/* Store Configuration */}
            <div style={sectionCard}>
                <h4 style={{ margin: 0, fontSize: '0.95rem', color: '#0f172a', fontWeight: '700' }}>🏪 Store Registration</h4>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px' }}>
                    <div>
                        <label style={labelStyle}>Store / Business Province (Nexus)</label>
                        <select value={cfg.storeProvince} onChange={e => update({ storeProvince: e.target.value })} style={inputStyle}>
                            {PROVINCES.map(p => (
                                <option key={p.code} value={p.code}>{p.name} ({p.code})</option>
                            ))}
                        </select>
                        {storeRates.taxType && (() => {
                            const b = TAX_TYPE_BADGE[storeRates.taxType];
                            return <span style={{ display: 'inline-block', marginTop: '6px', background: b.bg, color: b.color, border: `1px solid ${b.border}`, padding: '2px 10px', borderRadius: '10px', fontSize: '0.75rem', fontWeight: '700' }}>{b.label}</span>;
                        })()}
                    </div>

                    <div>
                        <label style={labelStyle}>GST/HST Registration Number (optional)</label>
                        <input
                            type="text"
                            placeholder="e.g. 123456789 RT0001"
                            value={cfg.registrationNumber}
                            onChange={e => update({ registrationNumber: e.target.value })}
                            style={inputStyle}
                        />
                        <span style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '4px', display: 'block' }}>Displayed on order confirmations.</span>
                    </div>
                </div>
            </div>

            {/* Collection Rules */}
            <div style={sectionCard}>
                <h4 style={{ margin: 0, fontSize: '0.95rem', color: '#0f172a', fontWeight: '700' }}>📋 Collection Rules</h4>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {[
                        { key: 'applyToAllProvinces',      label: 'Collect tax for all Canadian provinces & territories',    desc: 'Recommended. Tax is calculated for every Canadian shipping address.' },
                        { key: 'applyOnlyInStoreProvince', label: 'Collect tax only when customer is in the store province',  desc: 'Useful for small businesses below the $30,000 GST registration threshold.' },
                        { key: 'showTaxBreakdown',         label: 'Show tax line-item breakdown in cart summary',             desc: 'Displays individual GST / HST / PST / QST rows. If off, shows one combined "Tax" line.' },
                        { key: 'taxIncludedInPrice',       label: 'Prices are tax-inclusive (back-calculate tax from price)', desc: 'When ON, product prices already include tax; tax is extracted rather than added.' },
                    ].map(({ key, label, desc }) => (
                        <label key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', padding: '10px 12px', background: '#fff', borderRadius: '7px', border: '1px solid #e2e8f0' }}>
                            <input
                                type="checkbox"
                                checked={!!cfg[key]}
                                onChange={e => {
                                    if (key === 'applyToAllProvinces' && e.target.checked) update({ applyToAllProvinces: true, applyOnlyInStoreProvince: false });
                                    else if (key === 'applyOnlyInStoreProvince' && e.target.checked) update({ applyOnlyInStoreProvince: true, applyToAllProvinces: false });
                                    else update({ [key]: e.target.checked });
                                }}
                                style={{ marginTop: '2px', accentColor: '#3b82f6', flexShrink: 0 }}
                            />
                            <div>
                                <div style={{ fontWeight: '600', fontSize: '0.875rem', color: '#1e293b' }}>{label}</div>
                                <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '2px' }}>{desc}</div>
                            </div>
                        </label>
                    ))}
                </div>

                <div>
                    <label style={labelStyle}>Rounding Mode</label>
                    <select value={cfg.roundingMode} onChange={e => update({ roundingMode: e.target.value })} style={{ ...inputStyle, width: 'auto', minWidth: '200px' }}>
                        <option value="standard">Standard (nearest cent)</option>
                        <option value="up">Always round up</option>
                        <option value="down">Always round down</option>
                    </select>
                </div>
            </div>

            {/* Live Preview Calculator */}
            <div style={{ ...sectionCard, background: '#0f172a', border: '1px solid #1e293b' }}>
                <h4 style={{ margin: 0, fontSize: '0.95rem', color: '#f1f5f9', fontWeight: '700' }}>🧮 Live Tax Preview Calculator</h4>
                <p style={{ margin: 0, fontSize: '0.8rem', color: '#94a3b8' }}>See what a $100.00 subtotal would generate for any customer province, using your current settings.</p>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <div>
                        <label style={{ ...labelStyle, color: '#94a3b8' }}>Customer Province</label>
                        <select value={previewProv} onChange={e => setPreviewProv(e.target.value)} style={{ ...inputStyle, background: '#1e293b', color: '#f1f5f9', border: '1px solid #334155', width: 'auto' }}>
                            {PROVINCES.map(p => <option key={p.code} value={p.code}>{p.name} ({p.code})</option>)}
                        </select>
                    </div>
                    <div style={{ marginTop: '18px' }}>
                        <span style={{ background: previewBadge.bg, color: previewBadge.color, border: `1px solid ${previewBadge.border}`, padding: '3px 10px', borderRadius: '10px', fontSize: '0.78rem', fontWeight: '700' }}>
                            {previewBadge.label}
                        </span>
                    </div>
                </div>

                <div style={{ background: '#1e293b', borderRadius: '8px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: '#94a3b8', fontSize: '0.85rem' }}>
                        <span>Subtotal</span><span>$100.00</span>
                    </div>
                    {previewResult.appliesTax && cfg.enabled ? (
                        previewResult.breakdown.map((b, i) => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', color: '#60a5fa', fontSize: '0.85rem' }}>
                                <span>{b.label}</span><span>+${b.amount.toFixed(2)}</span>
                            </div>
                        ))
                    ) : (
                        <div style={{ color: '#64748b', fontSize: '0.85rem' }}>— No tax applies (module {cfg.enabled ? 'enabled, check rules' : 'disabled'})</div>
                    )}
                    <div style={{ borderTop: '1px solid #334155', paddingTop: '8px', display: 'flex', justifyContent: 'space-between', fontWeight: '800', fontSize: '1rem', color: '#f1f5f9' }}>
                        <span>Order Total</span>
                        <span>${(100 + (cfg.enabled ? previewResult.total : 0)).toFixed(2)}</span>
                    </div>
                    {previewResult.appliesTax && cfg.enabled && (
                        <div style={{ color: '#94a3b8', fontSize: '0.75rem' }}>Effective rate: {previewResult.effectiveRate}%</div>
                    )}
                </div>
            </div>

            {/* Province Rate Reference Table */}
            <div style={sectionCard}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '10px' }}>
                    <div>
                        <h4 style={{ margin: 0, fontSize: '0.98rem', color: '#0f172a', fontWeight: '800' }}>
                            📊 Province Rate Reference &amp; Individual Overrides
                        </h4>
                        <p style={{ margin: '4px 0 0 0', fontSize: '0.82rem', color: '#64748b' }}>
                            CRA rates are pre-loaded. Adjust <strong>GST</strong> and <strong>PST / QST / HST</strong> individually to account for special category items (e.g. <strong>BC 10% PST for alcohol/liquor</strong>, exemptions, or regional tax overrides).
                        </p>
                    </div>
                    {Object.keys(cfg.customRates || {}).length > 0 && (
                        <button
                            type="button"
                            onClick={() => update({ customRates: {} })}
                            style={{
                                background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca',
                                padding: '5px 12px', borderRadius: '6px', fontSize: '0.78rem', fontWeight: '700', cursor: 'pointer'
                            }}
                        >
                            Reset All Overrides
                        </button>
                    )}
                </div>

                {/* BC Liquor / Alcohol PST Callout & 1-Click Preset */}
                <div style={{
                    background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px',
                    padding: '10px 14px', fontSize: '0.82rem', color: '#0369a1', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '1.2rem' }}>🍷</span>
                        <span>
                            <strong>British Columbia Alcohol Sales Notice:</strong> In BC, liquor and alcoholic beverages are subject to <strong>10% PST</strong> (standard goods PST is 7%) + <strong>5% GST</strong> (15% total).
                        </span>
                    </div>
                    <button
                        type="button"
                        onClick={() => {
                            updateCustomComponent('BC', 'pst', '10');
                            updateCustomComponent('BC', 'gst', '5');
                        }}
                        style={{
                            background: '#0284c7', color: '#fff', border: 'none', padding: '6px 14px',
                            borderRadius: '6px', fontSize: '0.8rem', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap'
                        }}
                    >
                        Apply BC 10% Alcohol PST Preset
                    </button>
                </div>

                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                        <thead>
                            <tr style={{ background: '#f1f5f9', color: '#475569', fontWeight: '700' }}>
                                <th style={{ padding: '9px 12px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Province</th>
                                <th style={{ padding: '9px 10px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>Type</th>
                                <th style={{ padding: '9px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>CRA Baseline</th>
                                <th style={{ padding: '9px 10px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>GST Override %</th>
                                <th style={{ padding: '9px 10px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>PST / QST / HST Override %</th>
                                <th style={{ padding: '9px 12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>Effective Total</th>
                                <th style={{ padding: '9px 10px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {PROVINCES.map((p, i) => {
                                const r = PROVINCE_TAX_RATES[p.code];
                                const b = TAX_TYPE_BADGE[r.taxType];
                                const override = cfg.customRates?.[p.code];
                                const isOverridden = Boolean(override);

                                const gstDisplay = getComponentOverrideDisplay(override, 'gst', r.taxType);
                                const provDisplay = getComponentOverrideDisplay(override, 'provincial', r.taxType);

                                return (
                                    <tr key={p.code} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                                        {/* Province Name */}
                                        <td style={{ padding: '8px 12px', fontWeight: '600', color: '#1e293b' }}>
                                            {p.name} <span style={{ color: '#94a3b8', fontWeight: '400' }}>({p.code})</span>
                                        </td>

                                        {/* Tax Type Badge */}
                                        <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                                            <span style={{ background: b.bg, color: b.color, border: `1px solid ${b.border}`, padding: '2px 8px', borderRadius: '8px', fontSize: '0.72rem', fontWeight: '700' }}>
                                                {b.label}
                                            </span>
                                        </td>

                                        {/* CRA Baseline */}
                                        <td style={{ padding: '8px 10px', color: '#64748b', fontSize: '0.8rem' }}>
                                            {r.taxType === 'hst' && `HST ${(r.hst * 100).toFixed(0)}%`}
                                            {r.taxType === 'gst+pst' && `GST ${(r.gst * 100).toFixed(0)}% + PST ${(r.pst * 100).toFixed(0)}%`}
                                            {r.taxType === 'gst+qst' && `GST ${(r.gst * 100).toFixed(0)}% + QST ${(r.qst * 100).toFixed(3)}%`}
                                            {r.taxType === 'gst' && `GST ${(r.gst * 100).toFixed(0)}%`}
                                        </td>

                                        {/* GST Override % */}
                                        <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                                            {r.taxType === 'hst' ? (
                                                <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>— In HST</span>
                                            ) : (
                                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max="100"
                                                        step="any"
                                                        placeholder={((r.gst || 0) * 100).toString()}
                                                        value={gstDisplay}
                                                        onChange={e => updateCustomComponent(p.code, 'gst', e.target.value)}
                                                        style={{
                                                            width: '60px', padding: '4px 6px', borderRadius: '5px',
                                                            border: gstDisplay !== '' ? '2px solid #3b82f6' : '1px solid #cbd5e1',
                                                            background: gstDisplay !== '' ? '#eff6ff' : '#fff',
                                                            fontSize: '0.82rem', textAlign: 'center', fontWeight: gstDisplay !== '' ? '800' : '400'
                                                        }}
                                                    />
                                                    <span style={{ fontSize: '0.75rem', color: '#64748b' }}>%</span>
                                                </div>
                                            )}
                                        </td>

                                        {/* PST / QST / HST Override % */}
                                        <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                                            {r.taxType === 'gst' ? (
                                                <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>— None</span>
                                            ) : r.taxType === 'hst' ? (
                                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max="100"
                                                        step="any"
                                                        placeholder={((r.hst || 0) * 100).toString()}
                                                        value={provDisplay}
                                                        onChange={e => updateCustomComponent(p.code, 'hst', e.target.value)}
                                                        style={{
                                                            width: '60px', padding: '4px 6px', borderRadius: '5px',
                                                            border: provDisplay !== '' ? '2px solid #3b82f6' : '1px solid #cbd5e1',
                                                            background: provDisplay !== '' ? '#eff6ff' : '#fff',
                                                            fontSize: '0.82rem', textAlign: 'center', fontWeight: provDisplay !== '' ? '800' : '400'
                                                        }}
                                                    />
                                                    <span style={{ fontSize: '0.75rem', color: '#64748b' }}>% HST</span>
                                                </div>
                                            ) : r.taxType === 'gst+qst' ? (
                                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max="100"
                                                        step="any"
                                                        placeholder={((r.qst || 0) * 100).toString()}
                                                        value={provDisplay}
                                                        onChange={e => updateCustomComponent(p.code, 'qst', e.target.value)}
                                                        style={{
                                                            width: '60px', padding: '4px 6px', borderRadius: '5px',
                                                            border: provDisplay !== '' ? '2px solid #3b82f6' : '1px solid #cbd5e1',
                                                            background: provDisplay !== '' ? '#eff6ff' : '#fff',
                                                            fontSize: '0.82rem', textAlign: 'center', fontWeight: provDisplay !== '' ? '800' : '400'
                                                        }}
                                                    />
                                                    <span style={{ fontSize: '0.75rem', color: '#64748b' }}>% QST</span>
                                                </div>
                                            ) : (
                                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max="100"
                                                        step="any"
                                                        placeholder={((r.pst || 0) * 100).toString()}
                                                        value={provDisplay}
                                                        onChange={e => updateCustomComponent(p.code, 'pst', e.target.value)}
                                                        style={{
                                                            width: '60px', padding: '4px 6px', borderRadius: '5px',
                                                            border: provDisplay !== '' ? '2px solid #3b82f6' : '1px solid #cbd5e1',
                                                            background: provDisplay !== '' ? '#eff6ff' : '#fff',
                                                            fontSize: '0.82rem', textAlign: 'center', fontWeight: provDisplay !== '' ? '800' : '400'
                                                        }}
                                                    />
                                                    <span style={{ fontSize: '0.75rem', color: '#64748b' }}>% PST</span>
                                                    {p.code === 'BC' && (
                                                        <button
                                                            type="button"
                                                            title="Set BC Alcohol PST to 10%"
                                                            onClick={() => {
                                                                updateCustomComponent('BC', 'pst', '10');
                                                                if (!gstDisplay) updateCustomComponent('BC', 'gst', '5');
                                                            }}
                                                            style={{
                                                                background: provDisplay === '10' ? '#e0f2fe' : '#f1f5f9',
                                                                color: provDisplay === '10' ? '#0369a1' : '#475569',
                                                                border: provDisplay === '10' ? '1px solid #0284c7' : '1px solid #cbd5e1',
                                                                borderRadius: '4px', padding: '2px 6px', fontSize: '0.7rem', fontWeight: '700', cursor: 'pointer', marginLeft: '4px'
                                                            }}
                                                        >
                                                            10% 🍷
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </td>

                                        {/* Effective Total */}
                                        <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                                            <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                                                <span style={{ fontFamily: 'monospace', fontSize: '0.88rem', fontWeight: '800', color: isOverridden ? '#0284c7' : '#0f172a' }}>
                                                    {getProvinceEffectiveTotalDisplay(p.code)}
                                                </span>
                                                {isOverridden && (
                                                    <span style={{ fontSize: '0.65rem', fontWeight: '800', background: '#dcfce7', color: '#15803d', padding: '1px 5px', borderRadius: '4px' }}>
                                                        CUSTOM
                                                    </span>
                                                )}
                                            </div>
                                        </td>

                                        {/* Action */}
                                        <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                                            {isOverridden ? (
                                                <button
                                                    type="button"
                                                    onClick={() => resetProvinceOverride(p.code)}
                                                    title="Reset to default CRA rate"
                                                    style={{
                                                        background: '#f1f5f9', color: '#64748b', border: '1px solid #cbd5e1',
                                                        borderRadius: '5px', padding: '3px 8px', fontSize: '0.72rem', fontWeight: '700', cursor: 'pointer'
                                                    }}
                                                >
                                                    Reset
                                                </button>
                                            ) : (
                                                <span style={{ color: '#cbd5e1', fontSize: '0.72rem' }}>Default</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Save */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', paddingTop: '6px' }}>
                <button
                    onClick={handleSave}
                    style={{
                        background: '#0f172a', color: '#fff', border: 'none',
                        padding: '12px 26px', borderRadius: '8px', fontWeight: '700',
                        fontSize: '0.95rem', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
                    }}
                >
                    💾 Save Tax Settings
                </button>
                {saved && <span style={{ color: '#10b981', fontWeight: '700', fontSize: '0.9rem' }}>{saved}</span>}
            </div>
        </div>
    );
};

export default CanadianTaxManager;
